<template>
  <footer class="site-footer">
    <div class="footer-inner">
      <!-- Marca -->
      <div class="footer-brand">
        <div class="footer-logo"></div>
        <p class="footer-tagline">
          Software CMMS para equipos de mantenimiento en Colombia y LATAM.
        </p>
        <div class="footer-social">
          <a href="https://co.linkedin.com/company/fourin-sas" target="_blank" rel="noopener" aria-label="LinkedIn">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z"/></svg>
          </a>
          <a href="#" target="_blank" rel="noopener" aria-label="Twitter/X">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.46l8.6-9.83L0 1.15h7.6l5.24 6.93 6.06-6.93zm-1.29 19.5h2.04L6.48 3.24H4.29l13.32 17.41z"/></svg>
          </a>
          <a href="#" target="_blank" rel="noopener" aria-label="YouTube">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3.02 3.02 0 0 0 .5 6.2C0 8.08 0 12 0 12s0 3.92.5 5.8a3.02 3.02 0 0 0 2.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 0 0 2.12-2.14C24 15.92 24 12 24 12s0-3.92-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z"/></svg>
          </a>
          <a href="https://wa.me/573106643008" target="_blank" rel="noopener" aria-label="WhatsApp">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M.06 24l1.68-6.13A11.86 11.86 0 0 1 .16 11.9C.16 5.34 5.5 0 12.06 0a11.82 11.82 0 0 1 8.4 3.49 11.82 11.82 0 0 1 3.48 8.41c0 6.56-5.34 11.9-11.9 11.9a11.9 11.9 0 0 1-5.69-1.45L.06 24zM6.6 20.13l.36.21a9.86 9.86 0 0 0 5.02 1.38c5.45 0 9.89-4.43 9.89-9.88a9.83 9.83 0 0 0-2.9-6.99 9.83 9.83 0 0 0-6.98-2.9c-5.46 0-9.9 4.44-9.9 9.89 0 1.86.52 3.68 1.51 5.25l.24.38-1 3.63 3.76-.98zM17.5 14.3c-.07-.12-.26-.2-.55-.34-.29-.15-1.7-.84-1.96-.94-.26-.1-.46-.14-.65.14-.19.29-.74.94-.91 1.13-.17.19-.34.22-.62.07-.29-.14-1.21-.45-2.3-1.42-.85-.76-1.42-1.7-1.6-1.98-.16-.29-.01-.44.13-.58.13-.13.29-.34.43-.5.15-.18.19-.3.29-.5.1-.19.05-.36-.02-.5-.07-.15-.65-1.56-.89-2.14-.23-.56-.47-.48-.65-.49l-.55-.01c-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.38s1.02 2.76 1.17 2.96c.14.19 2.01 3.08 4.88 4.32.68.29 1.21.47 1.63.6.68.22 1.3.19 1.79.12.55-.08 1.7-.7 1.94-1.36.24-.67.24-1.24.17-1.36z"/></svg>
          </a>
        </div>
      </div>

      <!-- Columnas de links -->
      <nav class="footer-cols">
        <div v-for="col in columns" :key="col.title" class="footer-col" :class="{ open: col.open }">
          <button class="footer-col-head" @click="col.open = !col.open">
            {{ col.title }}
            <span class="footer-col-toggle">{{ col.open ? '−' : '+' }}</span>
          </button>
          <ul class="footer-col-links">
            <li v-for="link in col.links" :key="link.label">
              <a v-if="link.external" :href="link.to" target="_blank" rel="noopener">{{ link.label }}</a>
              <router-link v-else :to="link.to">{{ link.label }}</router-link>
            </li>
          </ul>
        </div>
      </nav>
    </div>

    <div class="footer-bottom">
      <p>© {{ year }} Fourin Track · Barranquilla, Atlántico, Colombia · Todos los derechos reservados</p>
      <p class="footer-made">Hecho con ❤️ en Colombia</p>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const year = new Date().getFullYear()

interface FooterLink { label: string; to: string; external?: boolean }
interface FooterColumn { title: string; open: boolean; links: FooterLink[] }

const columns = ref<FooterColumn[]>([
  {
    title: 'Producto',
    open: false,
    links: [
      { label: 'Características', to: '/#features' },
      { label: 'Precios', to: '/precios' },
      { label: 'Add-ons', to: '/precios' },
      { label: 'Integraciones', to: '/software-mantenimiento-cmms' },
    ],
  },
  {
    title: 'Empresa',
    open: false,
    links: [
      { label: 'Nuestra historia', to: '/about' },
      { label: 'Equipo', to: '/about' },
      { label: 'Contacto', to: 'mailto:info@fourin.co', external: true },
      { label: 'Trabaja con nosotros', to: '/contact' },
    ],
  },
  {
    title: 'Recursos',
    open: false,
    links: [
      { label: 'Blog', to: '/blog' },
      { label: 'Guías y tutoriales', to: '/blog' },
      { label: 'Ebook', to: '/ebook' },
      { label: 'Actualizaciones', to: '/blog' },
    ],
  },
  {
    title: 'Legal',
    open: false,
    links: [
      { label: 'Política de datos', to: '/Política de Tratamiento de Datos Personales.pdf', external: true },
      { label: 'Privacidad', to: '/Política de Tratamiento de Datos Personales.pdf', external: true },
      { label: 'Términos de uso', to: '/contact' },
    ],
  },
])
</script>

<style scoped>
.site-footer {
  background: #ffffff;
  color: #1A1A2E;
  font-family: 'Manrope', system-ui, sans-serif;
  border-top: 1px solid #EDF1F6;
}

.footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 28px 30px;
  display: grid;
  grid-template-columns: 1.4fr 2.6fr;
  gap: 48px;
}

/* Marca */
.footer-logo {
  background-image: url(/fourin.png);
  width: 140px;
  height: 44px;
  background-size: contain;
  background-position: left center;
  background-repeat: no-repeat;
  margin-bottom: 18px;
}

.footer-tagline {
  font-size: 14px;
  line-height: 1.6;
  color: #46566B;
  max-width: 280px;
  margin: 0 0 20px;
}

.footer-social {
  display: flex;
  gap: 14px;
}

.footer-social a {
  color: #46566B;
  transition: color 0.2s ease;
}

.footer-social svg {
  width: 22px;
  height: 22px;
  display: block;
}

.footer-social a:hover {
  color: #0c82ce;
}

/* Columnas */
.footer-cols {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px;
}

.footer-col-head {
  background: none;
  border: none;
  color: #129bef;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  padding: 0 0 14px;
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: default;
}

.footer-col-toggle {
  display: none;
}

.footer-col-links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.footer-col-links a {
  color: #46566B;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s ease;
}

.footer-col-links a:hover {
  color: #129bef;
}

/* Copyright */
.footer-bottom {
  border-top: 1px solid #EDF1F6;
  padding: 22px 28px;
  text-align: center;
  font-size: 13px;
  color: #46566B;
}

.footer-bottom p {
  margin: 0;
}

.footer-made {
  margin-top: 6px !important;
}

/* Mobile: accordion */
@media (max-width: 768px) {
  .footer-inner {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 40px 22px 20px;
  }

  .footer-cols {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .footer-col {
    border-bottom: 1px solid #EDF1F6;
  }

  .footer-col-head {
    padding: 16px 0;
    cursor: pointer;
  }

  .footer-col-toggle {
    display: inline-flex;
    font-size: 20px;
    font-weight: 400;
    color: #129bef;
  }

  .footer-col-links {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease, padding 0.3s ease;
  }

  .footer-col.open .footer-col-links {
    max-height: 320px;
    padding-bottom: 16px;
  }
}
</style>
